"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight, Heart, Home, RefreshCw } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

export interface ErrorCardAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: "primary" | "outline";
  icon?: React.ReactNode;
}

export interface ErrorCardProps {
  title?: string;
  message?: React.ReactNode;
  dogImage?: string;
  actions?: ErrorCardAction[];
  supportEmail?: string;
  helpText?: string;
  /** Label for the mailto link after `helpText`. Defaults to "Contact support!". */
  supportLinkText?: string;
  showBackButton?: boolean;
  showHomeButton?: boolean;
  showRetryButton?: boolean;
  onRetry?: () => void;
  linkText?: string;
  linkUrl?: string;
  missingFields?: string[];
  sparkleType?: "sparkles" | "hearts";
}

/**
 * The inner error card (mascot, title, message, actions, help text) without the
 * full-page background wrapper — so it can be reused inside other containers (e.g. a modal).
 * @param props - Error card content configuration; see {@link ErrorCardProps}.
 * @returns The bordered, padded error card content.
 */
export function ErrorCardContent({
  title = "Woof! Page not found",
  message = "Looks like this page went on a walk and got lost! Don't worry, our pup will help you find your way back.",
  dogImage = "/nowadays-nerd.png",
  actions,
  supportEmail = "support@nowadays.ai",
  helpText = "Still can't find what you're looking for?",
  supportLinkText = "Contact support!",
  showBackButton = true,
  showHomeButton = true,
  showRetryButton = false,
  onRetry,
  linkText,
  linkUrl,
  missingFields,
  sparkleType = "sparkles",
}: ErrorCardProps) {
  // Hooks
  const router = useRouter();

  // Handlers
  const handleGoHome = () => {
    router.push("/");
  };

  const handleGoBack = () => {
    router.back();
  };

  // Derived values
  const defaultActions: ErrorCardAction[] = [];
  if (showBackButton) {
    defaultActions.push({
      label: "Go back",
      onClick: handleGoBack,
      variant: "outline",
      icon: <ArrowLeft className="w-4 h-4" />,
    });
  }
  if (showRetryButton && onRetry) {
    defaultActions.push({
      label: "Try again",
      onClick: onRetry,
      variant: "outline",
      icon: <RefreshCw className="w-4 h-4" />,
    });
  }
  if (showHomeButton) {
    defaultActions.push({
      label: "Go home",
      onClick: handleGoHome,
      variant: "primary",
      icon: <Home className="w-4 h-4" />,
    });
  }

  const finalActions = actions || defaultActions;

  // Render
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-3xl border border-gray-200 p-8 md:p-12">
      <div className="flex flex-col items-center text-center">
        {/* Dog mascot */}
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.1,
          }}
          className="mb-6"
        >
          <div className="relative">
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Image src={dogImage} alt="Nowadays Dog" width={140} height={140} className="drop-shadow-lg" />
            </motion.div>

            {/* Sparkles or hearts around the dog */}
            {sparkleType === "sparkles" ? (
              <>
                <motion.div
                  animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                  className="absolute -top-1 left-8"
                >
                  <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
                </motion.div>

                <motion.div
                  animate={{ scale: [0.6, 1.1, 0.6], opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay: 0.8 }}
                  className="absolute top-2 left-12"
                >
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                </motion.div>

                <motion.div
                  animate={{ scale: [0.7, 1.0, 0.7], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2.2, repeat: Infinity, delay: 1.5 }}
                  className="absolute bottom-4 -right-2"
                >
                  <div className="w-1.5 h-1.5 bg-orange-300 rounded-full"></div>
                </motion.div>
              </>
            ) : (
              <>
                <motion.div
                  animate={{ y: [-5, -15, -5], opacity: [0.7, 1, 0.7], rotate: [0, 15, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                  className="absolute -top-2 -right-2"
                >
                  <Heart className="w-4 h-4 text-pink-400 fill-current" />
                </motion.div>

                <motion.div
                  animate={{ y: [-3, -13, -3], opacity: [0.5, 1, 0.5], rotate: [0, -10, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: 0.7 }}
                  className="absolute top-4 -left-4"
                >
                  <Heart className="w-3 h-3 text-pink-300 fill-current" />
                </motion.div>

                <motion.div
                  animate={{ y: [-4, -12, -4], opacity: [0.6, 1, 0.6], rotate: [0, 12, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay: 1.2 }}
                  className="absolute bottom-2 right-2"
                >
                  <Heart className="w-3 h-3 text-red-300 fill-current" />
                </motion.div>
              </>
            )}
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-4 text-landing-primary font-bold text-2xl"
        >
          {title}
        </motion.h1>

        {/* Message */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="body-lg text-landing-secondary max-w-md mb-6 font-medium"
        >
          {message}
        </motion.p>

        {/* Missing fields list if applicable */}
        {missingFields && missingFields.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="w-full max-w-md mb-6 p-4 rounded-xl bg-yellow-50 border border-yellow-200"
          >
            <p className="font-semibold mb-3 text-landing-primary">Missing or invalid fields:</p>
            <ul className="space-y-2 text-left">
              {missingFields.map((field, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.05 }}
                  className="flex items-start gap-2"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-yellow-500" />
                  <span className="body-md text-landing-secondary">{field}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Actions */}
        {finalActions.length > 0 && !linkUrl && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 w-full max-w-md"
          >
            {finalActions.map((action, index) => (
              <FetchButton
                key={index}
                onClick={action.onClick ?? (action.href ? () => router.push(action.href!) : undefined)}
                variant={action.variant || "primary"}
                color="primary"
                size="lg"
                leftIcon={action.icon}
                className="flex-1"
              >
                {action.label}
              </FetchButton>
            ))}
          </motion.div>
        )}

        {/* Link-based actions (alternative to button actions) */}
        {linkUrl && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 w-full max-w-md"
          >
            {showBackButton && (
              <button
                onClick={handleGoBack}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-landing-secondary/20 text-landing-secondary rounded-full hover:bg-landing-secondary/5 transition-all duration-200 font-medium group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Go Back
              </button>
            )}

            {linkText && (
              <Link
                href={linkUrl}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl group flex-1"
              >
                {linkUrl === "/" ? <Home className="w-4 h-4" /> : null}
                {linkText}
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
          </motion.div>
        )}

        {/* Help text */}
        {helpText && supportEmail && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 text-sm text-gray-500 font-medium"
          >
            {helpText}{" "}
            <a href={`mailto:${supportEmail}`} className="underline hover:text-landing-primary transition-colors">
              {supportLinkText}
            </a>
          </motion.p>
        )}
      </div>
    </div>
  );
}
